import clsx from 'clsx';
import Link from 'next/link';
import { useTooltip, TooltipPosition } from '@/context/Ui';
import { Spinner } from '../Spinner';

export interface ButtonProps {
  className?: string;
  kind?: 'button' | 'submit';
  size?: 'sm' | 'md' | 'lg';
  style?: 'primary' | 'secondary' | 'warning';
  onClick?: () => void;
  href?: string;
  disabled?: boolean;
  loading?: boolean;
  lessPadding?: boolean;
  tooltipText?: string;
  tooltipPosition?: TooltipPosition;
  ariaLabel?: string;
  ignoreRouter?: boolean;
}

export const Button = ({
  kind = 'button',
  size = 'md',
  style = 'primary',
  children,
  className,
  onClick,
  href,
  disabled = false,
  loading = false,
  lessPadding = false,
  tooltipText,
  tooltipPosition = 'top',
  ariaLabel,
  ignoreRouter = false,
}: React.PropsWithChildren<ButtonProps>) => {
  const { tooltipElementRef } = useTooltip(tooltipText, {
    position: tooltipPosition,
  });

  const classes = clsx(
    'inline-flex items-center justify-center rounded-md border border-green-dark',
    size === 'sm' && 'text-xs md:text-sm leading-7',
    size === 'md' && 'text-sm md:text-md leading-10',
    size === 'lg' && 'text-base md:text-lg leading-10',
    size === 'sm' && lessPadding && 'px-1 md:px-2',
    size === 'sm' && !lessPadding && 'px-1 md:px-3',
    size === 'md' && lessPadding && 'px-3 md:px-4',
    size === 'md' && !lessPadding && 'px-6 md:px-9',
    size === 'lg' && lessPadding && 'px-6 md:px-8',
    size === 'lg' && !lessPadding && 'px-12 md:px-16',
    style === 'primary' &&
      'bg-pt-primary text-pt-primary-child hover:bg-pt-secondary hover:text-pt-secondary-child hover:border-pt-secondary',
    style === 'secondary' &&
      'bg-pt-secondary text-pt-secondary-child hover:bg-pt-secondary-accent hover:border-pt-secondary-accent',
    style === 'warning' &&
      'bg-red-600 text-white border-red-600 hover:bg-red-700 hover:border-red-700',
  );

  return href ? (
    ignoreRouter ? (
      <a
        ref={
          tooltipText
            ? (tooltipElementRef as React.RefObject<HTMLAnchorElement | null>)
            : undefined
        }
        className={clsx(classes, className)}
        href={href}
        aria-label={ariaLabel}
      >
        {children}
        {loading ? (
          <span className="inline ml-3 ">
            <Spinner size="sm" />
          </span>
        ) : null}
      </a>
    ) : (
      <Link
        ref={
          tooltipText
            ? (tooltipElementRef as React.RefObject<HTMLAnchorElement | null>)
            : undefined
        }
        className={clsx(classes, className)}
        href={href}
        aria-label={ariaLabel}
      >
        {children}
        {loading ? (
          <span className="inline ml-3 ">
            <Spinner size="sm" />
          </span>
        ) : null}
      </Link>
    )
  ) : (
    <button
      ref={
        tooltipText
          ? (tooltipElementRef as React.RefObject<HTMLButtonElement | null>)
          : undefined
      }
      type={kind}
      className={clsx(
        classes,
        'disabled:opacity-50 disabled:cursor-not-allowed',
        className,
      )}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      {children}
      {loading ? (
        <span className="inline ml-3 ">
          <Spinner size="sm" />
        </span>
      ) : null}
    </button>
  );
};
